import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './Toast.module.css';
import { translate } from '../../utils/i18n';

// Toast Context
const ToastContext = createContext(null);

// Toast 类型
const TOAST_TYPES = {
    success: {
        icon: '✓',
        className: 'success'
    },
    error: {
        icon: '✕',
        className: 'error'
    },
    warning: {
        icon: '⚠',
        className: 'warning'
    },
    info: {
        icon: 'ℹ',
        className: 'info'
    }
};

// 单个 Toast 项
const ToastItem = ({ id, message, type, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            onClose(id);
        }, 3000);
        return () => clearTimeout(timer);
    }, [id, onClose]);

    const toastType = TOAST_TYPES[type] || TOAST_TYPES.info;

    return (
        <motion.div
            className={`${styles.toast} ${styles[toastType.className]}`}
            initial={{ opacity: 0, y: -50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            transition={{ duration: 0.3 }}
        >
            <span className={styles.icon}>{toastType.icon}</span>
            <span className={styles.message}>{message}</span>
            <button className={styles.closeBtn} onClick={() => onClose(id)}>
                ✕
            </button>
        </motion.div>
    );
};

// Toast 容器
const ToastContainer = ({ toasts, removeToast }) => {
    return (
        <div className={styles.container}>
            <AnimatePresence>
                {toasts.map((toast) => (
                    <ToastItem
                        key={toast.id}
                        {...toast}
                        onClose={removeToast}
                    />
                ))}
            </AnimatePresence>
        </div>
    );
};

// Toast Provider
export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const removeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    const addToast = useCallback((message, type = 'info') => {
        const id = Date.now() + Math.random();
        setToasts((prev) => [...prev, { id, message, type }]);
        // 手动触发一次翻译，让自动翻译库可以识别新加入的提示文案
        try {
            if (translate && typeof translate.execute === 'function') {
                // 放到下一帧，等 DOM 更新后再执行
                setTimeout(() => {
                    try {
                        translate.execute();
                    } catch (e) {
                        // 忽略翻译执行错误，避免影响业务逻辑
                    }
                }, 0);
            }
        } catch {
            // ignore
        }
        return id;
    }, []);

    const toast = {
        show: (message) => addToast(message, 'info'),
        success: (message) => addToast(message, 'success'),
        error: (message) => addToast(message, 'error'),
        warning: (message) => addToast(message, 'warning'),
        info: (message) => addToast(message, 'info'),
    };

    return (
        <ToastContext.Provider value={toast}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </ToastContext.Provider>
    );
};

// useToast Hook
export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export default ToastProvider;
